<template>
	<section v-loading="loading" class="provider-store-close-page">
		<div class="page-heading">
			<div>
				<h2>闭店申请</h2>
				<p>提交申请后，平台审核通过才会关闭店铺</p>
			</div>
			<el-button icon="el-icon-back" @click="handleBack">返回我的店铺</el-button>
		</div>

		<div class="close-warning">
			<span class="warning-icon"><i class="el-icon-warning-outline"></i></span>
			<div>
				<strong>提交前请确认</strong>
				<p>闭店审核通过后，店铺将变为已闭店状态，不能继续提交资料变更。请确认当前订单、服务和售后事项已处理完毕。</p>
			</div>
		</div>

		<el-form ref="closeForm" :model="formData" :rules="formRules" label-position="top" class="close-form">
			<div class="close-section">
				<div class="close-section-heading">
					<span class="heading-icon"><i class="el-icon-s-shop"></i></span>
					<div>
						<strong>店铺信息</strong>
						<small>确认本次申请关闭的店铺</small>
					</div>
				</div>
				<div class="shop-info-grid">
					<div class="shop-info-item">
						<span>店铺名称</span>
						<strong>{{ shopInfo.shopName || '-' }}</strong>
					</div>
					<div class="shop-info-item">
						<span>经营状态</span>
						<strong class="normal-state">{{ shopStateText }}</strong>
					</div>
				</div>
			</div>

			<div class="close-section">
				<div class="close-section-heading">
					<span class="heading-icon is-warning"><i class="el-icon-document"></i></span>
					<div>
						<strong>闭店原因</strong>
						<small>请如实说明申请关闭店铺的原因</small>
					</div>
				</div>
				<el-form-item prop="closeReason">
					<el-input
						v-model.trim="formData.closeReason"
						type="textarea"
						:rows="6"
						maxlength="300"
						show-word-limit
						resize="none"
						placeholder="请输入闭店原因"
					></el-input>
				</el-form-item>
			</div>

			<div class="form-actions">
				<el-button @click="handleBack">取消</el-button>
				<el-button type="danger" :loading="submitLoading" @click="handleSubmit">提交闭店申请</el-button>
			</div>
		</el-form>
	</section>
</template>

<script>
	import { mapGetters } from 'vuex'
	import { closeSubmitMerchantShopApply } from '@/api/mall'
	import { ENUM_SHOP_STATE, SHOP_AUDIT_STATE, SHOP_APPLY_TYPE, SHOP_STATE } from '@/enum'
	import { getUuid } from '@/utils'

	export default {
		name: 'ProviderStoreClose',
		data() {
			return {
				// 页面初始化校验及店铺状态刷新时的加载状态。
				loading: false,
				// 防止闭店申请重复提交，并控制提交按钮加载效果。
				submitLoading: false,
				formData: {
					// 申请 ID 同时用于后端业务记录及提交后的状态同步。
					id: getUuid(),
					closeReason: ''
				},
				formRules: {
					closeReason: [{ required: true, message: '请输入闭店原因', trigger: 'blur' }]
				}
			}
		},
		computed: {
			...mapGetters(['merchantShopInfo', 'merchantLatestApply']),
			// 对状态管理中的店铺数据设置空对象兜底，供模板及提交参数使用。
			shopInfo() {
				return this.merchantShopInfo || {}
			},
			// 将店铺状态枚举转换为页面展示文案。
			shopStateText() {
				return ENUM_SHOP_STATE[this.shopInfo.shopState] || '-'
			}
		},
		created() {
			this.initPage()
		},
		methods: {
			// 刷新店铺上下文，并完成经营状态及重复申请两项前置校验。
			async initPage() {
				this.loading = true
				try {
					// 页面初始化时重新查询，防止用户通过地址栏进入后使用过期的店铺状态。
					await this.$store.dispatch('merchantShop/loadShopContext', true)
					if (!this.shopInfo.id || Number(this.shopInfo.shopState) !== SHOP_STATE.NORMAL) {
						this.$message.warning('只有正常经营的店铺才能提交闭店申请')
						this.$router.replace('/provider/store')
						return
					}
					// 任意类型的待审核店铺申请都未结束时，不允许重复提交闭店申请。
					if (this.merchantLatestApply && Number(this.merchantLatestApply.auditState) === SHOP_AUDIT_STATE.PENDING) {
						this.$message.warning('已有待审核申请，请等待平台审核')
						this.$router.replace('/provider/store')
					}
				} finally {
					this.loading = false
				}
			},
			// 闭店流程中的所有返回操作统一回到新服务商端“我的店铺”。
			handleBack() {
				this.$router.push('/provider/store')
			},
			// 表单校验通过后再次向用户确认，确认后才调用闭店申请接口。
			handleSubmit() {
				this.$refs.closeForm.validate(valid => {
					if (!valid) return
					this.$modal
						.confirm('提交后需等待平台审核，确定提交闭店申请吗？')
						.then(() => this.submitCloseApply())
						.catch(() => {})
				})
			},
			// 按旧版接口字段提交闭店申请，并同步最新申请状态后返回店铺页。
			async submitCloseApply() {
				this.submitLoading = true
				try {
					await closeSubmitMerchantShopApply({
						id: this.formData.id,
						shopId: this.shopInfo.id,
						applyType: SHOP_APPLY_TYPE.CLOSE,
						closeReason: this.formData.closeReason,
						remark: this.formData.closeReason
					})
					this.$message.success('闭店申请已提交，请等待平台审核')
					await this.$store.dispatch('merchantShop/loadShopContext', true)
					// 接口数据尚未同步时先写入本次申请，确保店铺页立即进入待审核状态。
					if (
						this.merchantLatestApply.id !== this.formData.id ||
						Number(this.merchantLatestApply.auditState) !== SHOP_AUDIT_STATE.PENDING
					) {
						this.$store.commit('merchantShop/SET_LATEST_APPLY', {
							id: this.formData.id,
							shopId: this.shopInfo.id,
							applyType: SHOP_APPLY_TYPE.CLOSE,
							auditState: SHOP_AUDIT_STATE.PENDING,
							closeReason: this.formData.closeReason,
							remark: this.formData.closeReason
						})
					}
					// 返回店铺页后会展示待审核提示，同时阻止再次进入闭店申请页面。
					this.$router.replace('/provider/store')
				} finally {
					this.submitLoading = false
				}
			}
		}
	}
</script>

<style lang="scss" scoped>
	.provider-store-close-page {
		min-height: 100%;
		padding: 24px;
		border: 1px solid #e4e9ef;
		border-radius: 8px;
		background: #ffffff;
		box-sizing: border-box;
	}

	.page-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding-bottom: 19px;
		border-bottom: 1px solid #e9edf2;

		h2 {
			margin: 0 0 6px;
			color: #303846;
			font-size: 21px;
		}
		p {
			margin: 0;
			color: #929aa6;
			font-size: 13px;
		}
	}

	.close-warning {
		display: flex;
		align-items: center;
		margin-top: 18px;
		padding: 16px 18px;
		border: 1px solid #f3cd90;
		border-radius: 8px;
		color: #9a6617;
		background: linear-gradient(90deg, #fff8e8, #fffbf2);

		.warning-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 40px;
			height: 40px;
			margin-right: 13px;
			border-radius: 50%;
			font-size: 21px;
			background: #ffedc7;
		}
		strong {
			display: block;
			font-size: 15px;
		}
		p {
			margin: 5px 0 0;
			color: #92794f;
			font-size: 13px;
			line-height: 21px;
		}
	}

	.close-section {
		margin-top: 18px;
		padding: 20px 22px;
		border: 1px solid #e5eaf0;
		border-radius: 8px;
		background: #ffffff;
		box-shadow: 0 5px 18px rgba(38, 71, 109, 0.04);
	}

	.close-section-heading {
		display: flex;
		align-items: center;
		margin-bottom: 18px;
		padding-bottom: 15px;
		border-bottom: 1px solid #e7ecf2;

		.heading-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 38px;
			height: 38px;
			margin-right: 11px;
			border-radius: 9px;
			color: #2671cb;
			font-size: 19px;
			background: #eaf3fd;

			&.is-warning {
				color: #c57a17;
				background: #fff0d5;
			}
		}
		strong,
		small {
			display: block;
		}
		strong {
			color: #2f3b4d;
			font-size: 18px;
			line-height: 24px;
		}
		small {
			margin-top: 3px;
			color: #98a1ae;
			font-size: 12px;
			font-weight: normal;
		}
	}

	.shop-info-grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 16px;
	}

	.shop-info-item {
		padding: 14px 16px;
		border: 1px solid #edf0f4;
		border-radius: 6px;
		background: #f8fafc;

		span {
			display: block;
			margin-bottom: 8px;
			color: #7e8998;
			font-size: 13px;
		}
		strong {
			color: #344154;
			font-size: 16px;
			line-height: 25px;
		}
		.normal-state {
			color: #168653;
		}
	}

	.close-form {
		::v-deep .el-form-item {
			margin-bottom: 0;
		}
		::v-deep .el-textarea__inner {
			padding: 13px 15px;
			border-color: #dbe3ec;
			font-size: 14px;
			line-height: 23px;
		}
	}

	.form-actions {
		padding: 26px 0 5px;
		text-align: center;

		.el-button {
			min-width: 130px;
		}
	}
</style>
